import * as Y from 'yjs';
import { newDataKey } from '../crypto/vault.ts';
import { wrapDataKey } from '../crypto/session.ts';
import { store } from '../data/db.ts';
import { acquire, onDocsChanged, release, withDoc } from '../data/docs.ts';
import { emptyNote } from '../data/notes.ts';
import { deviceZone, nextOccurrence, type Repeat } from './parse.ts';

/**
 * Erinnerungen liegen in einem einzigen Yjs-Dokument (docs/KONZEPT-ERINNERUNGEN.md,
 * Abschnitt 2), nicht in je einer Notiz. Hundert Dokumente für je acht Wörter wären
 * hundert Update-Logs und hundert Schlüssel.
 *
 * Das Dokument ist technisch eine Notizzeile mit fester Kennung — damit trägt der
 * bestehende Abgleich es unverändert mit: eigener Datenschlüssel, verschlüsseltes
 * Update-Log, Snapshots, Offline-Warteschlange. Aus der Notizliste wird es beim Laden
 * herausgefiltert (`store.refresh`).
 */

/** Feste Kennung. Muss ein gültiges UUID-Format haben, der Server prüft darauf. */
export const TODO_DOC_ID = '70d05000-0000-4000-8000-000000000001';

export type Todo = {
  id: string;
  text: string;
  done: boolean;
  doneAt: number | null;
  dueAt: number | null;
  repeat: Repeat | null;
  /**
   * Zeitzone, in der der Termin gemeint ist (IANA). Ein Termin ist eine Wanduhrzeit
   * an einem Ort — ohne die Zone wandert eine Wiederholung über die Zeitumstellung
   * und über Reisen hinweg. Alte Einträge haben keine; dann gilt die des Geräts.
   */
  tz: string | null;
  createdAt: number;
  /** Reserviert für die spätere Brücke zu einer Notiz. */
  noteId: string | null;
};

/** Erledigtes verschwindet nach dreißig Tagen — ein Häkchen ist damit nicht sofort endgültig. */
const KEEP_DONE_MS = 30 * 24 * 60 * 60 * 1000;

type Item = Y.Map<unknown>;

function read(item: Item): Todo {
  return {
    id: String(item.get('id') ?? ''),
    text: String(item.get('text') ?? ''),
    done: item.get('done') === true,
    doneAt: (item.get('doneAt') as number | undefined) ?? null,
    dueAt: (item.get('dueAt') as number | undefined) ?? null,
    repeat: (item.get('repeat') as Repeat | undefined) ?? null,
    tz: (item.get('tz') as string | undefined) ?? null,
    createdAt: (item.get('createdAt') as number | undefined) ?? 0,
    noteId: (item.get('noteId') as string | undefined) ?? null,
  };
}

/** Legt die Zeile an, falls es sie noch nicht gibt. Der Server nimmt sonst keine Deltas. */
async function ensureRow(): Promise<void> {
  const existing = await store.getNote(TODO_DOC_ID);
  if (existing) return;
  const now = Date.now();
  await store.putNote({
    ...emptyNote(TODO_DOC_ID, wrapDataKey(newDataKey()), now),
    dirty: true,
  });
}

/**
 * Schreibzugriff — legt die Zeile bei Bedarf an. Lesen tut das ausdrücklich nicht:
 * sonst entstünde allein durch die Zählung in der Notizliste ein Dokument, das der
 * Abgleich anschließend zum Server trägt, obwohl nie eine Erinnerung angelegt wurde.
 */
async function items<T>(action: (list: Y.Array<Item>, doc: Y.Doc) => T): Promise<T> {
  await ensureRow();
  return withDoc(TODO_DOC_ID, (entry) => action(entry.doc.getArray<Item>('items'), entry.doc));
}

export const todos = {
  async all(): Promise<Todo[]> {
    if (!(await store.getNote(TODO_DOC_ID))) return [];
    return withDoc(TODO_DOC_ID, (entry) =>
      entry.doc.getArray<Item>('items').toArray().map(read),
    );
  },

  async add(fields: {
    text: string;
    dueAt?: number | null;
    repeat?: Repeat | null;
    tz?: string | null;
  }): Promise<string> {
    const id = crypto.randomUUID();
    await items((list, doc) => {
      const item = new Y.Map<unknown>();
      doc.transact(() => {
        item.set('id', id);
        item.set('text', fields.text);
        item.set('done', false);
        item.set('createdAt', Date.now());
        if (fields.dueAt != null) {
          item.set('dueAt', fields.dueAt);
          item.set('tz', fields.tz ?? deviceZone());
        }
        if (fields.repeat) item.set('repeat', fields.repeat);
        list.push([item]);
      });
    });
    return id;
  },

  async patch(id: string, fields: Partial<Omit<Todo, 'id'>>): Promise<void> {
    await items((list, doc) => {
      const item = list.toArray().find((entry) => entry.get('id') === id);
      if (!item) return;
      doc.transact(() => {
        for (const [key, value] of Object.entries(fields)) {
          if (value === null) item.delete(key);
          else item.set(key, value);
        }
      });
    });
  },

  /**
   * Abhaken. Eine wiederkehrende Erinnerung wird nicht erledigt, sondern rückt auf
   * den nächsten Termin — sonst müsste man sie jede Woche neu anlegen.
   */
  async toggle(id: string): Promise<void> {
    await items((list, doc) => {
      const item = list.toArray().find((entry) => entry.get('id') === id);
      if (!item) return;
      const current = read(item);
      doc.transact(() => {
        if (!current.done && current.repeat && current.dueAt != null) {
          item.set('dueAt', nextOccurrence(current.dueAt, current.repeat, current.tz ?? undefined));
          return;
        }
        item.set('done', !current.done);
        if (current.done) item.delete('doneAt');
        else item.set('doneAt', Date.now());
      });
    });
  },

  async remove(id: string): Promise<void> {
    await items((list, doc) => {
      const index = list.toArray().findIndex((entry) => entry.get('id') === id);
      if (index >= 0) doc.transact(() => list.delete(index, 1));
    });
  },

  /** Verschiebt einen Eintrag an eine neue Position. Yjs kennt kein Move, also neu einsetzen. */
  async move(id: string, to: number): Promise<void> {
    await items((list, doc) => {
      const current = list.toArray();
      const from = current.findIndex((entry) => entry.get('id') === id);
      if (from < 0 || from === to) return;
      const fields = read(current[from]!);
      doc.transact(() => {
        list.delete(from, 1);
        const copy = new Y.Map<unknown>();
        for (const [key, value] of Object.entries(fields)) {
          if (value !== null) copy.set(key, value);
        }
        list.insert(Math.max(0, Math.min(to, list.length)), [copy]);
      });
    });
  },

  /** Räumt lange Erledigtes weg. Läuft beim Öffnen des Bereichs, nicht im Hintergrund. */
  async prune(): Promise<number> {
    if (!(await store.getNote(TODO_DOC_ID))) return 0;
    return items((list, doc) => {
      const cutoff = Date.now() - KEEP_DONE_MS;
      const stale = list
        .toArray()
        .map((entry, index) => ({ todo: read(entry), index }))
        .filter(({ todo }) => todo.done && todo.doneAt != null && todo.doneAt < cutoff);
      if (stale.length === 0) return 0;
      doc.transact(() => {
        // Von hinten löschen, sonst verschieben sich die noch offenen Positionen.
        for (const { index } of stale.reverse()) list.delete(index, 1);
      });
      return stale.length;
    });
  },

  /**
   * Hält das Dokument offen, solange der Bereich sichtbar ist. Auch das legt noch
   * nichts an — wer nur hineinschaut, soll keine Zeile hinterlassen.
   */
  async open(): Promise<() => void> {
    await acquire(TODO_DOC_ID);
    return () => release(TODO_DOC_ID);
  },

  /** Meldet Änderungen — auch solche, die über den Abgleich hereinkommen. */
  onChange(listener: () => void): () => void {
    return onDocsChanged((id) => {
      if (id === TODO_DOC_ID) listener();
    });
  },
};

/** Fällig und noch offen — Grundlage für die Kennzeichnung in der Navigation. */
export function dueCount(list: Todo[], now = Date.now()): number {
  return list.filter((todo) => !todo.done && todo.dueAt != null && todo.dueAt <= now).length;
}
